import { useContext, useCallback, useMemo } from 'react';
import { PhoneFlowContext, PhoneFlowResult } from './context';
import { UseBaseFlowResult } from '../shared/useBaseFlow';
import { useGetAccountInformation } from '../../hooks/queries/useGetAccountInformation';
import { dispatchAddPhoneCloseEvent, dispatchAddPhoneOpenEvent } from '../../utils/analyticsEvents';
import { hasVerifiedEmail, hasVerifiedPhone } from '../shared/helpers/claims';
import { ClaimFlowData } from '../shared/types/ClaimFlowData';
import { ClaimTypes } from '../shared/enums/ClaimTypes';
import { VoidFn } from '../shared/types/VoidFn';

const usePhoneFlow = (): PhoneFlowResult => {
  const { data } = useGetAccountInformation();
  const baseFlow = useContext(PhoneFlowContext) as UseBaseFlowResult<ClaimFlowData>;

  const hasPhone = useMemo(() => hasVerifiedPhone(data?.authenticationClaims ?? []), [data]);
  const hasEmail = useMemo(() => hasVerifiedEmail(data?.authenticationClaims ?? []), [data]);

  const open = useCallback(
    (fn: VoidFn) => {
      baseFlow.callback.current = fn;

      if (!hasPhone && hasEmail) {
        baseFlow.open();
        dispatchAddPhoneOpenEvent();
        return;
      }

      fn();
    },
    [baseFlow, hasPhone, hasEmail],
  );

  const handleClose = (): void => {
    baseFlow.close();
    dispatchAddPhoneCloseEvent();
  };

  return { ...baseFlow, claimType: ClaimTypes.CELLPHONE, open, close: handleClose };
};

export { usePhoneFlow };
